// src/pm/extensionRenderer.ts
import {
  getAttributesFromExtensions,
  getExtensionField,
  getSchemaByResolvedExtensions,
  resolveExtensions,
  splitExtensions
} from "@tiptap/core";
import { Node } from "@tiptap/pm/model";

// src/helpers.ts
import { mergeAttributes } from "@tiptap/core";
function getAttributes(nodeOrMark, extensionAttributes, onlyRenderedAttributes) {
  const nodeOrMarkAttributes = nodeOrMark.attrs;
  if (!nodeOrMarkAttributes) {
    return {};
  }
  return extensionAttributes.filter((item) => {
    if (item.type !== (typeof nodeOrMark.type === "string" ? nodeOrMark.type : nodeOrMark.type.name)) {
      return false;
    }
    if (onlyRenderedAttributes) {
      return item.attribute.rendered;
    }
    return true;
  }).map((item) => {
    if (!item.attribute.renderHTML) {
      return {
        [item.name]: item.name in nodeOrMarkAttributes ? nodeOrMarkAttributes[item.name] : item.attribute.default
      };
    }
    return item.attribute.renderHTML(nodeOrMarkAttributes) || {
      [item.name]: item.name in nodeOrMarkAttributes ? nodeOrMarkAttributes[item.name] : item.attribute.default
    };
  }).reduce((attributes, attribute) => mergeAttributes(attributes, attribute), {});
}
function getHTMLAttributes(nodeOrMark, extensionAttributes) {
  return getAttributes(nodeOrMark, extensionAttributes, true);
}

// src/pm/extensionRenderer.ts
function mapNodeExtensionToReactNode(domOutputSpecToElement, extension, extensionAttributes, options) {
  const context = {
    name: extension.name,
    options: extension.options,
    storage: extension.storage,
    parent: extension.parent
  };
  const renderToHTML = getExtensionField(extension, "renderHTML", context);
  if (!renderToHTML) {
    if (options == null ? void 0 : options.unhandledNode) {
      return [extension.name, options.unhandledNode];
    }
    return [
      extension.name,
      () => {
        throw new Error(
          `[tiptap error]: Node ${extension.name} cannot be rendered, it is missing a "renderToHTML" method, please implement it or override the corresponding "nodeMapping" method to have a custom rendering`
        );
      }
    ];
  }
  return [
    extension.name,
    ({ node, children }) => {
      try {
        return domOutputSpecToElement(
          renderToHTML({
            node,
            HTMLAttributes: getHTMLAttributes(node, extensionAttributes)
          })
        )(children);
      } catch (e) {
        throw new Error(
          `[tiptap error]: Node ${extension.name} cannot be rendered, it's "renderToHTML" method threw an error: ${e.message}`,
          { cause: e }
        );
      }
    }
  ];
}
function mapMarkExtensionToReactNode(domOutputSpecToElement, extension, extensionAttributes, options) {
  const context = {
    name: extension.name,
    options: extension.options,
    storage: extension.storage,
    parent: extension.parent
  };
  const renderToHTML = getExtensionField(extension, "renderHTML", context);
  if (!renderToHTML) {
    if (options == null ? void 0 : options.unhandledMark) {
      return [extension.name, options.unhandledMark];
    }
    return [
      extension.name,
      () => {
        throw new Error(`Node ${extension.name} cannot be rendered, it is missing a "renderToHTML" method`);
      }
    ];
  }
  return [
    extension.name,
    ({ mark, children }) => {
      try {
        return domOutputSpecToElement(
          renderToHTML({
            mark,
            HTMLAttributes: getHTMLAttributes(mark, extensionAttributes)
          })
        )(children);
      } catch (e) {
        throw new Error(
          `[tiptap error]: Mark ${extension.name} cannot be rendered, it's "renderToHTML" method threw an error: ${e.message}`,
          { cause: e }
        );
      }
    }
  ];
}
function renderToElement({
  renderer,
  domOutputSpecToElement,
  mapDefinedTypes,
  content,
  extensions,
  options
}) {
  extensions = resolveExtensions(extensions);
  const extensionAttributes = getAttributesFromExtensions(extensions);
  const { nodeExtensions, markExtensions } = splitExtensions(extensions);
  if (!(content instanceof Node)) {
    content = Node.fromJSON(getSchemaByResolvedExtensions(extensions), content);
  }
  return renderer({
    ...options,
    nodeMapping: {
      ...Object.fromEntries(
        nodeExtensions.filter((e) => {
          if (e.name in mapDefinedTypes) {
            return false;
          }
          if (options == null ? void 0 : options.nodeMapping) {
            return !(e.name in options.nodeMapping);
          }
          return true;
        }).map(
          (nodeExtension) => mapNodeExtensionToReactNode(domOutputSpecToElement, nodeExtension, extensionAttributes, options)
        )
      ),
      ...mapDefinedTypes,
      ...options == null ? void 0 : options.nodeMapping
    },
    markMapping: {
      ...Object.fromEntries(
        markExtensions.filter((e) => {
          if (options == null ? void 0 : options.markMapping) {
            return !(e.name in options.markMapping);
          }
          return true;
        }).map((mark) => mapMarkExtensionToReactNode(domOutputSpecToElement, mark, extensionAttributes, options))
      ),
      ...options == null ? void 0 : options.markMapping
    }
  })({ content });
}

// src/pm/react/react.ts
import React2 from "react";

// src/json/react/react.ts
import React from "react";

// src/json/renderer.ts
function TiptapStaticRenderer(renderComponent, {
  nodeMapping,
  markMapping,
  unhandledNode,
  unhandledMark
}) {
  return function renderContent({
    content,
    parent
  }) {
    var _a;
    const nodeType = typeof content.type === "string" ? content.type : content.type.name;
    const NodeHandler = (_a = nodeMapping[nodeType]) != null ? _a : unhandledNode;
    if (!NodeHandler) {
      throw new Error(`missing handler for node type ${nodeType}`);
    }
    const nodeContent = renderComponent({
      component: NodeHandler,
      props: {
        node: content,
        parent,
        renderElement: renderContent,
        // Lazily compute the children to avoid unnecessary recursion
        get children() {
          const children = [];
          if (content.content) {
            content.content.forEach((child) => {
              children.push(
                renderContent({
                  content: child,
                  parent: content
                })
              );
            });
          }
          return children;
        }
      }
    });
    const markedContent = content.marks ? content.marks.reduce((acc, mark) => {
      var _a2;
      const markType = typeof mark.type === "string" ? mark.type : mark.type.name;
      const MarkHandler = (_a2 = markMapping[markType]) != null ? _a2 : unhandledMark;
      if (!MarkHandler) {
        throw new Error(`missing handler for mark type ${markType}`);
      }
      return renderComponent({
        component: MarkHandler,
        props: {
          mark,
          parent,
          node: content,
          children: acc
        }
      });
    }, nodeContent) : nodeContent;
    return markedContent;
  };
}

// src/json/react/react.ts
function renderJSONContentToReactElement(options) {
  let key = 0;
  return TiptapStaticRenderer(({ component, props: { children, ...props } }) => {
    return React.createElement(
      component,
      // eslint-disable-next-line no-plusplus
      Object.assign(props, { key: key++ }),
      [].concat(children)
    );
  }, options);
}

// src/pm/react/react.ts
function mapAttrsToHTMLAttributes(attrs, key) {
  if (!attrs) {
    return { key };
  }
  return Object.entries(attrs).reduce(
    (acc, [name, value]) => {
      if (name === "class") {
        return Object.assign(acc, { className: value });
      }
      if (name === "style" && typeof value === "string") {
        const styleObject = {};
        value.split(";").forEach((style) => {
          const [styleKey, val] = style.split(":");
          if (styleKey && val) {
            const camelCaseKey = styleKey.trim().replace(/-([a-z])/g, (g) => g[1].toUpperCase());
            styleObject[camelCaseKey] = val.trim();
          }
        });
        return Object.assign(acc, { style: styleObject });
      }
      return Object.assign(acc, { [name]: value });
    },
    { key }
  );
}
function domOutputSpecToReactElement(content, key = 0) {
  if (typeof content === "string") {
    return () => content;
  }
  if (typeof content === "object" && "length" in content) {
    let [tag, attrs, children, ...rest] = content;
    const parts = tag.split(" ");
    if (parts.length > 1) {
      tag = parts[1];
      if (attrs === void 0) {
        attrs = {
          xmlns: parts[0]
        };
      }
      if (attrs === 0) {
        attrs = {
          xmlns: parts[0]
        };
        children = 0;
      }
      if (typeof attrs === "object") {
        attrs = Object.assign(attrs, { xmlns: parts[0] });
      }
    }
    if (attrs === void 0) {
      return () => React2.createElement(tag, mapAttrsToHTMLAttributes(void 0, key.toString()));
    }
    if (attrs === 0) {
      return (child) => React2.createElement(tag, mapAttrsToHTMLAttributes(void 0, key.toString()), child);
    }
    if (typeof attrs === "object") {
      if (Array.isArray(attrs)) {
        if (children === void 0) {
          return (child) => React2.createElement(
            tag,
            mapAttrsToHTMLAttributes(void 0, key.toString()),
            domOutputSpecToReactElement(attrs, key++)(child)
          );
        }
        if (children === 0) {
          return (child) => React2.createElement(
            tag,
            mapAttrsToHTMLAttributes(void 0, key.toString()),
            domOutputSpecToReactElement(attrs, key++)(child)
          );
        }
        return (child) => React2.createElement(
          tag,
          mapAttrsToHTMLAttributes(void 0, key.toString()),
          domOutputSpecToReactElement(attrs)(child),
          [children].concat(rest).map((outputSpec) => domOutputSpecToReactElement(outputSpec, key++)(child))
        );
      }
      if (children === void 0) {
        return () => React2.createElement(tag, mapAttrsToHTMLAttributes(attrs, key.toString()));
      }
      if (children === 0) {
        return (child) => React2.createElement(tag, mapAttrsToHTMLAttributes(attrs, key.toString()), child);
      }
      return (child) => React2.createElement(
        tag,
        mapAttrsToHTMLAttributes(attrs, key.toString()),
        [children].concat(rest).map((outputSpec) => domOutputSpecToReactElement(outputSpec, key++)(child))
      );
    }
  }
  throw new Error(
    "[tiptap error]: Unsupported DomOutputSpec type, check the `renderHTML` method output or implement a node mapping",
    {
      cause: content
    }
  );
}
function renderToReactElement({
  content,
  extensions,
  options
}) {
  return renderToElement({
    renderer: renderJSONContentToReactElement,
    domOutputSpecToElement: domOutputSpecToReactElement,
    mapDefinedTypes: {
      // Map a doc node to concatenated children
      doc: ({ children }) => React2.createElement(React2.Fragment, {}, children),
      // Map a text node to its text content
      text: ({ node }) => {
        var _a;
        return (_a = node.text) != null ? _a : "";
      }
    },
    content,
    extensions,
    options
  });
}
export {
  domOutputSpecToReactElement,
  mapAttrsToHTMLAttributes,
  mapMarkExtensionToReactNode,
  mapNodeExtensionToReactNode,
  renderToElement,
  renderToReactElement
};
//# sourceMappingURL=index.js.map